/**
 * A bounded-concurrency pool for the guest upload queue.
 *
 * `runPool` runs an async task over a queue that is *read live*: workers pull
 * one item each, run it, pull the next, until the queue is exhausted. With
 * three workers over the upload pipeline, the client-side preparation of the
 * next photograph (decode, thumbnail, HEIC conversion) overlaps the network
 * phase of the previous one, which is where the felt speed-up comes from.
 *
 * Contract, pinned by scripts/verify/verify-upload-pool.ts:
 *
 *   - at most `limit` tasks are in flight at any moment, and the limit is
 *     actually worked, not merely permitted
 *   - every item the queue surfaces is attempted exactly once
 *   - the pool returns only once the queue is drained fully
 *   - a task that rejects does not starve the queue: the worker moves on and
 *     `onError` (when given) is told why. The upload page uses this as a
 *     safety net only — its task is expected to settle its own row (set it to
 *     *failed*) and not reject at all.
 *
 * The queue check (`pull` returning null) ends a worker's loop; a pool whose
 * limit is zero or negative degrades to exactly one worker rather than none,
 * because a queue that quietly never drains is a worse failure than running
 * one at a time.
 */

export async function runPool<T>(
  /** Maximum tasks in flight. Zero or negative means one. */
  limit: number,
  /** Pull the next outstanding item, or return null when nothing is left. */
  pull: () => T | null,
  /** Send one item through its pipeline. */
  task: (item: T) => Promise<void>,
  /** Called when `task` rejects. The worker continues regardless. */
  onError?: (error: unknown, item: T) => void,
): Promise<void> {
  const workers = Math.max(1, Math.floor(limit));

  const run = async (): Promise<void> => {
    for (;;) {
      const item = pull();
      if (item === null || item === undefined) return;
      try {
        await task(item);
      } catch (error) {
        onError?.(error, item);
      }
    }
  };

  await Promise.all(Array.from({ length: workers }, run));
}
